import { ref, shallowRef } from 'vue';
import { Client, type ChannelStream } from '@ts-ssh2/core';
import { WebSocketTransport } from '@ts-ssh2/transport-ws';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export type HopConfig = {
	hostname: string;
	port: number;
	user: string;
	password?: string;
	privateKey?: string;
	passphrase?: string;
};

export type ConnectionConfig = {
	gatewayUrl: string;
	hostname: string;
	port: number;
	user: string;
	password?: string;
	privateKey?: string;
	passphrase?: string;
	hops?: HopConfig[];
};

export function useSsh() {
	const state = ref<ConnectionState>('disconnected');
	const error = ref<string | null>(null);
	const hasShell = ref(false);
	const hasSftp = ref(false);
	const client = shallowRef<Client | null>(null);

	let clients: Client[] = [];
	let abortController: AbortController | null = null;

	async function connect(config: ConnectionConfig) {
		disconnect();
		state.value = 'connecting';
		error.value = null;

		abortController = new AbortController();
		const signal = abortController.signal;

		try {
			const transport = new WebSocketTransport(config.gatewayUrl);

			// build the hop chain: hops (if any) + final destination
			const hops = config.hops ?? [];
			const allSteps: HopConfig[] = [
				...hops,
				{
					hostname: config.hostname,
					port: config.port,
					user: config.user,
					password: config.password,
					privateKey: config.privateKey,
					passphrase: config.passphrase,
				},
			];

			// connect through each hop, forwarding through the previous client
			let finalClient: Client | null = null;

			for (let i = 0; i < allSteps.length; i++) {
				const step = allSteps[i]!;
				const hopClient = new Client({ debug: true });
				clients.push(hopClient);

				if (i === 0) {
					// first hop connects via the websocket transport
					await hopClient.connect(
						{
							transport,
							hostname: step.hostname,
							port: step.port,
							user: step.user,
							password: step.password,
							privateKey: step.privateKey,
							passphrase: step.passphrase,
						},
						signal,
					);
				} else {
					// subsequent hops tunnel through the previous client
					const prevClient = clients[i - 1]!;
					const tunnel = await prevClient.forwardOut(step.hostname, step.port, signal);

					await hopClient.connect(
						{
							stream: tunnel,
							user: step.user,
							password: step.password,
							privateKey: step.privateKey,
							passphrase: step.passphrase,
						},
						signal,
					);
				}

				finalClient = hopClient;
			}

			client.value = finalClient;

			// probe capabilities in parallel before marking as connected,
			// so watchers see correct hasSftp/hasShell values
			const probeShell = finalClient!
				.shell({
					tty: { term: 'xterm-256color', cols: 80, rows: 24 },
					signal,
				})
				.then((ch) => {
					ch.close().catch(() => {});
					hasShell.value = true;
				})
				.catch(() => {
					hasShell.value = false;
				});

			const probeSftp = finalClient!
				.sftp()
				.then((sftp) => {
					sftp.dispose();
					hasSftp.value = true;
				})
				.catch(() => {
					hasSftp.value = false;
				});

			await Promise.all([probeShell, probeSftp]);

			state.value = 'connected';
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			error.value = message;
			state.value = 'error';
			disposeClients();
		}
	}

	async function openShell(options?: { cols: number; rows: number }): Promise<ChannelStream> {
		if (!client.value) throw new Error('Not connected');
		const cols = options?.cols ?? 80;
		const rows = options?.rows ?? 24;
		return client.value.shell({
			tty: { term: 'xterm-256color', cols, rows },
			signal: abortController?.signal,
		});
	}

	function disposeClients() {
		// dispose in reverse order so tunnels close cleanly
		for (let i = clients.length - 1; i >= 0; i--) {
			clients[i]!.dispose();
		}
		clients = [];
	}

	function disconnect() {
		abortController?.abort();
		abortController = null;
		disposeClients();
		client.value = null;
		hasShell.value = false;
		hasSftp.value = false;
		if (state.value !== 'error') {
			state.value = 'disconnected';
		}
	}

	return {
		state,
		error,
		hasShell,
		hasSftp,
		client,
		connect,
		openShell,
		disconnect,
	};
}
